import { ReactNode, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { customer360Service } from '@/data/customer360Service';
import type {
  CustomerMembership,
  CustomerMembershipTransaction,
  CustomerStampProgress,
  CustomerVoucher,
  PointsTransaction
} from '@/types/Customer360.interface';
import { formatMoney } from '@/utils/salesReport';
import {
  formatDateToDayMonthYear,
  formatDateToDayMonthYearTime
} from '@/utils/dateFormatters';
import { preparePurchaseTypeLabel } from '@/utils/helpers';

/** Loads once on mount (the tab is mounted when opened). */
function useLoad<T>(load: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setData(null);
    setFailed(false);
    load()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, failed };
}

const State = ({
  failed,
  loading,
  empty,
  emptyText,
  children
}: {
  failed: boolean;
  loading: boolean;
  empty: boolean;
  emptyText: string;
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  if (failed) {
    return (
      <Typography color="text.secondary" align="center">
        {t('customerCard.loadFailed')}
      </Typography>
    );
  }
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (empty) {
    return (
      <Typography color="text.secondary" align="center">
        {emptyText}
      </Typography>
    );
  }
  return <>{children}</>;
};

export const MembershipsPanel = ({ userId }: { userId: number }) => {
  const { t } = useTranslation();
  const { data, failed } = useLoad(
    () => customer360Service.getMemberships(userId),
    [userId]
  );
  const memberships: CustomerMembership[] = data?.memberships ?? [];
  const transactions: CustomerMembershipTransaction[] =
    data?.transactions ?? [];
  return (
    <State
      failed={failed}
      loading={!data && !failed}
      empty={memberships.length === 0}
      emptyText={t('customerCard.noMemberships')}
    >
      <Table size="small" aria-label={t('customerCard.tabs.memberships')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('customerCard.col.plan')}</TableCell>
            <TableCell>{t('branch')}</TableCell>
            <TableCell>{t('customerCard.col.period')}</TableCell>
            <TableCell align="right">
              {t('customerCard.col.remaining')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {memberships.map((membership) => (
            <TableRow key={membership.id}>
              <TableCell>{membership.membershipPlan?.name ?? '—'}</TableCell>
              <TableCell>{membership.branch?.name ?? '—'}</TableCell>
              <TableCell>
                {formatDateToDayMonthYear(membership.startDate)} –{' '}
                {formatDateToDayMonthYear(membership.endDate)}
              </TableCell>
              <TableCell align="right">
                {t('customerCard.days', { n: membership.remainingDayCount })}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {transactions.length > 0 && (
        <>
          <Typography variant="h5" sx={{ mt: 3, mb: 1 }}>
            {t('customerCard.membershipHistory')}
          </Typography>
          <Table size="small">
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow key={transaction.id}>
                  <TableCell>
                    {formatDateToDayMonthYearTime(transaction.createdAt)}
                  </TableCell>
                  <TableCell>
                    {t(`customerCard.membershipKind.${transaction.kind}`)}
                  </TableCell>
                  <TableCell>
                    {t('customerCard.days', { n: transaction.durationDays })}
                  </TableCell>
                  <TableCell>
                    {t(preparePurchaseTypeLabel(transaction.purchaseType))}
                  </TableCell>
                  <TableCell align="right">
                    {formatMoney(transaction.amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </State>
  );
};

export const StampCardsPanel = ({ userId }: { userId: number }) => {
  const { t } = useTranslation();
  const { data, failed } = useLoad(
    () => customer360Service.getStampCards(userId),
    [userId]
  );
  const cards: CustomerStampProgress[] = data ?? [];
  return (
    <State
      failed={failed}
      loading={!data && !failed}
      empty={cards.length === 0}
      emptyText={t('customerCard.noStampCards')}
    >
      {cards.map((progress) => {
        const required = Math.max(progress.stampCard.requiredStamps, 1);
        return (
          <Box key={progress.id} sx={{ mb: 2 }}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography fontWeight={600}>
                {progress.stampCard.name}
                {!progress.stampCard.isActive &&
                  ` · ${t('customerCard.inactiveCard')}`}
              </Typography>
              <Typography color="text.secondary">
                {t('customerCard.stamps', {
                  current: progress.currentStamps,
                  required,
                  cycles: progress.completedCycles
                })}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, (progress.currentStamps / required) * 100)}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>
        );
      })}
    </State>
  );
};

export const VouchersPanel = ({
  userId,
  branchId
}: {
  userId: number;
  branchId: number | null;
}) => {
  const { t } = useTranslation();
  const { data, failed } = useLoad(
    () => customer360Service.getVouchers(userId, branchId),
    [userId, branchId]
  );
  const vouchers: CustomerVoucher[] = data ?? [];
  return (
    <State
      failed={failed}
      loading={!data && !failed}
      empty={vouchers.length === 0}
      emptyText={t('customerCard.noVouchers')}
    >
      <Table size="small" aria-label={t('customerCard.tabs.vouchers')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('customerCard.col.voucher')}</TableCell>
            <TableCell>{t('customerCard.col.code')}</TableCell>
            <TableCell>{t('branch')}</TableCell>
            <TableCell>{t('customerCard.col.expires')}</TableCell>
            <TableCell align="right">{t('customerCard.col.left')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {vouchers.map((voucher) => (
            <TableRow key={voucher.id}>
              <TableCell>{voucher.voucherTemplate.name}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace' }}>
                {voucher.code}
              </TableCell>
              <TableCell>{voucher.branch?.name ?? '—'}</TableCell>
              <TableCell>
                {formatDateToDayMonthYear(voucher.expiresAt)}
              </TableCell>
              <TableCell align="right">
                {voucher.totalRemainingQuantity} / {voucher.totalRewardQuantity}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </State>
  );
};

const PAGE_SIZE = 20;

export const PointsHistoryPanel = ({
  userId,
  companyId
}: {
  userId: number;
  companyId: number | null;
}) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<PointsTransaction[]>([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  // Each page is appended exactly once, tied to the page it was asked for.
  const loadPage = async (next: number) => {
    if (!companyId) {
      setRows([]);
      setTotal(0);
      setPage(1);
      return;
    }
    setLoading(true);
    try {
      const result = await customer360Service.getPointsHistory(
        userId,
        companyId,
        next,
        PAGE_SIZE
      );
      setRows((previous) =>
        next === 1 ? result.transactions : [...previous, ...result.transactions]
      );
      setTotal(result.total);
      setPage(next);
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, companyId]);

  return (
    <State
      failed={failed}
      loading={loading && rows.length === 0}
      empty={page > 0 && rows.length === 0}
      emptyText={t('customerCard.noPoints')}
    >
      <Table size="small" aria-label={t('customerCard.tabs.points')}>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>
                {formatDateToDayMonthYearTime(row.createdAt)}
              </TableCell>
              <TableCell>{row.description ?? row.source}</TableCell>
              <TableCell>{row.branch?.name ?? '—'}</TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 600,
                  color: row.points >= 0 ? 'success.main' : 'error.main'
                }}
              >
                {row.points > 0 ? '+' : ''}
                {row.points.toLocaleString('tr-TR')}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {rows.length < total && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Button onClick={() => loadPage(page + 1)} disabled={loading}>
            {t('customerCard.loadMore')}
          </Button>
        </Box>
      )}
    </State>
  );
};
